import styled from "styled-components";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) => {
  if (totalPages <= 0) return null;

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;

    if (totalPages <= 7) {
      // 총 페이지가 7개 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 항상 첫 페이지 표시
      pages.push(1);

      // 현재 페이지 기준으로 표시할 범위 계산
      let startPage = Math.max(2, currentPage - Math.floor(maxButtons / 2));
      let endPage = Math.min(totalPages - 1, startPage + maxButtons - 1);

      // 끝 페이지 기준으로 시작 페이지 조정
      if (endPage === totalPages - 1) {
        startPage = Math.max(2, endPage - maxButtons + 1);
      }

      // 첫 페이지와 시작 페이지 사이에 생략 표시
      if (startPage > 2) {
        pages.push("...");
      }

      // 중간 페이지들
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // 끝 페이지와 마지막 페이지 사이에 생략 표시
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // 항상 마지막 페이지 표시
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <PaginationContainer>
      <NavButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        이전
      </NavButton>

      {pageNumbers.map((page, index) => {
        if (page === "...") {
          return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
        }

        return (
          <PageButton
            key={page}
            $active={page === currentPage}
            onClick={() => onPageChange(page as number)}
          >
            {page}
          </PageButton>
        );
      })}

      <NavButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        다음
      </NavButton>
    </PaginationContainer>
  );
};

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 40px;

  @media (max-width: 768px) {
    gap: 5px;
    margin-top: 30px;
  }
`;

const NavButton = styled.button<{ disabled: boolean }>`
  padding: 10px 16px;
  background: ${(props) => (props.disabled ? "#f5f5f5" : "#fff")};
  color: ${(props) => (props.disabled ? "#ccc" : "#666")};
  border: 1px solid ${(props) => (props.disabled ? "#e0e0e0" : "#ddd")};
  border-radius: 4px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #004094;
    color: #004094;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const PageButton = styled.button<{ $active: boolean }>`
  padding: 10px 14px;
  background: ${(props) => (props.$active ? "#004094" : "#fff")};
  color: ${(props) => (props.$active ? "#fff" : "#666")};
  border: 1px solid ${(props) => (props.$active ? "#004094" : "#ddd")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  transition: all 0.3s;
  min-width: 42px;

  &:hover {
    background: ${(props) => (props.$active ? "#004094" : "#f5f5f5")};
    border-color: #004094;
    color: ${(props) => (props.$active ? "#fff" : "#004094")};
  }

  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: 13px;
    min-width: 36px;
  }
`;

const Ellipsis = styled.span`
  padding: 10px 8px;
  color: #999;
  font-size: 14px;
  font-weight: 500;
  user-select: none;

  @media (max-width: 768px) {
    padding: 8px 4px;
    font-size: 13px;
  }
`;

export default Pagination;
